import { Box, styled, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes, gridClasses } from '@mui/x-data-grid-pro'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import GenericTable from 'src/components/table/GenericTable'
import { ILocalityEquipmentMaintenance } from 'src/generated-types/maintenance'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { GetRGBA, extractPlainText, getRecurrenceText, getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import CreateMaintenanceForm from './CreateMaintenanceForm'
import { FlexSpinner } from 'src/components/Spinner'
import { StyledChip } from 'src/screens/localities/deviations/DeviationDetailSection'

export interface MaintenanceTableProps {
  maintenances: ILocalityEquipmentMaintenance[]
  setMaintenances: React.Dispatch<React.SetStateAction<ILocalityEquipmentMaintenance[]>>
  modifiedMaintenanceIds?: number[]
  isUpdating?: boolean
}

const MaintenanceTable: React.FC<MaintenanceTableProps> = ({
  maintenances,
  setMaintenances,
  modifiedMaintenanceIds = [],
  isUpdating = false,
}) => {
  const { t: translate } = useTranslation(['maintenance', 'common'])
  const theme = useTheme()
  const { showDialog, showConfirmDialog } = useDialogCtx()

  const rows = useMemo(() => {
    return maintenances.map(maintenance => ({
      ...maintenance,
      isModified: modifiedMaintenanceIds.includes(maintenance.id),
    }))
  }, [maintenances, modifiedMaintenanceIds])

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'name',
      headerName: translate('maintenance-name', { ns: 'maintenance' }),
      flex: 1,
    },
    {
      field: 'note',
      headerName: translate('note', { ns: 'maintenance' }),
      flex: 1,
      renderCell: params => {
        const extractedNote = extractPlainText(params.row.note)
        return <DynamicTextTypography maxLength={25} text={extractedNote} fontWeight={400} />
      },
    },
    {
      field: 'gracePeriod',
      headerName: translate('gracePeriod', { ns: 'maintenance' }),
      flex: 1,
    },
    {
      field: 'frequency',
      headerName: translate('frequency', { ns: 'maintenance' }),
      flex: 1,
      renderCell: params => {
        const formattedFrequency = getRecurrenceText(
          params.row.repeatType,
          params.row.frequency,
          params.row.weekDays.toString(),
        )
        return <DynamicTextTypography maxLength={25} text={formattedFrequency} fontWeight={400} />
      },
    },
    {
      field: 'modifiedStatus',
      headerName: '',
      flex: 0.3,
      renderCell: params => {
        return params.row.isModified ? (
          <StyledChip variant="filled" size="small" label={translate('edited', { ns: 'common' })} />
        ) : null
      },
    },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 10,
        getActions: ({ id, row }) => [
          <GridActionsCellItem
            key={id}
            label={translate('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onEdit(row)}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key={id}
            label={translate('delete', { ns: 'common' })}
            icon={<MdDelete size={24} />}
            onClick={() => onDelete(id)}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [translate],
  )

  const updateMaintenanceSet = (maintenance: ILocalityEquipmentMaintenance, id) => {
    setMaintenances((prevItems: ILocalityEquipmentMaintenance[]) => {
      const newItems = [...prevItems]
      const index = newItems.findIndex(item => item.id === id)
      newItems[index] = { ...newItems[index], ...maintenance, ...{ id: id } }
      return [...newItems]
    })
  }
  const onEdit = row => {
    showDialog(
      CreateMaintenanceForm,
      {
        componentProps: {
          addMaintenance: maintenance => {
            updateMaintenanceSet(maintenance, row.id)
          },
          initialValues: row,
          isLocalityMaintenance: true,
          onSuccess: pop => {
            pop()
          },
        },
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
  }

  const onDelete = useCallback(id => {
    showConfirmDialog(
      translate('delete-maintenance', { ns: 'maintenance' }),
      translate('delete-maintenance-text', { ns: 'maintenance' }),
      {
        acceptText: translate('yes', { ns: 'common' }),
        cancelText: translate('no', { ns: 'common' }),
      },
      async pop => {
        setMaintenances((prevItems: ILocalityEquipmentMaintenance[]) => {
          const newItems = [...prevItems.filter(m => m.id != id)]
          return [...newItems]
        })
        pop()
      },
    )
  }, [])

  return (
    <Box position="relative">
      {isUpdating && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          sx={{
            bgcolor: GetRGBA(getThemeColor(theme, MUIThemeColors.white), 0.6),
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <FlexSpinner />
        </Box>
      )}
      <StyledMaintenanceTable
        id={'maintenances'}
        data={rows}
        columns={columns}
        enablePagination={false}
        hideDefaultFooterPagination={true}
        getRowClassName={params => (params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd')}
        autoHeight={false}
        height={'40vh'}
      />
    </Box>
  )
}

export default MaintenanceTable

const StyledMaintenanceTable = styled(GenericTable)({
  [`& .${gridClasses.main}`]: {
    paddingBottom: '8px',
  },
})
